<svelte:options runes={true} />

<script lang="ts">
	import deepEqual from "deep-equal";
	import { Badge } from "flowbite-svelte-v1";
	import { tick } from "svelte";
	import { twMerge } from "tailwind-merge";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";

	interface Props {
		stringTokens: string[];
		context: string;
		disabled?: boolean;
		useGrayOutline?: boolean;
		onchange: (tokens: string[]) => void;
		onBlur?: () => void;
	}

	const MAX_TOKENS = 1000;

	let { stringTokens, context, disabled = false, useGrayOutline = false, onchange, onBlur }: Props = $props();
	let input: HTMLInputElement | undefined = $state(undefined);
	let editInput: HTMLInputElement | undefined = $state(undefined);
	let isInputFocused = $state(false);
	let pendingTokens = $state(stringTokens);
	let explicitlyEnteringMultiple = $state(false);

	let editState: Record<string, { selected: boolean | undefined; isEditing: boolean | undefined }> = $state({}); // can make this type better

	$effect(() => watchStringTokens(stringTokens));

	function watchStringTokens(tokens: string[]) {
		pendingTokens = tokens;
		if (tokens.length > 1) {
			explicitlyEnteringMultiple = true;
			if (input) {
				input.value = "";
			}
		} else if (tokens.length === 1) {
			if (input && !explicitlyEnteringMultiple) {
				input.value = tokens[0];
			}
		}
	}

	function removeStringToken(index: number) {
		pendingTokens = pendingTokens.filter((_, i) => i !== index);
		handleChange();
	}

	function focusInput() {
		if (disabled) return;
		isInputFocused = true;
		input?.focus();
		if (pendingTokens.length === 1 && input && !explicitlyEnteringMultiple) {
			input.value = pendingTokens[0];
		}
	}

	function checkToDelimitTokens() {
		if (disabled) return;
		if (!isInputFocused) {
			return;
		}
		if (input && input.value.length && pendingTokens.length < MAX_TOKENS) {
			pendingTokens = [...pendingTokens, input.value];
			if (explicitlyEnteringMultiple) {
				input.value = "";
			}
		}
	}

	function handleKeydown(event: KeyboardEvent) {
		if (disabled) return;
		// backspace
		if (event.key === "Backspace" && (!input?.value || !explicitlyEnteringMultiple)) {
			const editing = pendingTokens.pop();
			if (editing && input) {
				input.value = editing;
				input.focus();
				if (pendingTokens.length === 0) {
					explicitlyEnteringMultiple = false;
				}
				event.preventDefault();
			}
		}

		// enter
		if (event.key === "Enter") {
			if (input && input?.value) {
				if (input.value.trim().length > 0 && pendingTokens.length < MAX_TOKENS) {
					explicitlyEnteringMultiple = true;
					pendingTokens = [...pendingTokens, input.value];
					input.value = "";
				}

				event.preventDefault();
			}
		}

		if (input) {
			input.size = Math.max(1, input.value.length) + 4;
		}
	}

	async function handlePaste(e: ClipboardEvent) {
		if (disabled) return;
		if (e.clipboardData) {
			e.clipboardData
				.getData("text")
				.trim()
				.split(/[\s,;]+/)
				.forEach(str => {
					if (pendingTokens.length < MAX_TOKENS) {
						pendingTokens = [...pendingTokens, str];
					}
				});
			e.preventDefault();
		}
	}

	function handleInputBlur() {
		if (disabled) return;
		checkToDelimitTokens();
		isInputFocused = false;
		handleChange();
		onBlur?.();
	}

	function handleChange() {
		if (!deepEqual(stringTokens, pendingTokens)) {
			const removeDuplicates = pendingTokens.filter((token, index, self) => self.indexOf(token) === index);
			onchange(removeDuplicates);
		}
	}

	function initiateEdit(event: Event, stringToken: string) {
		if (disabled) return;
		event.stopPropagation();
		editState[stringToken] = { ...editState[stringToken], isEditing: true };
		editState = { ...editState };
		tick().then(() => {
			editInput?.focus();
		});
	}

	function handleEditFocus(e: FocusEvent) {
		if (disabled) return;
		e.preventDefault();
		editInput?.setSelectionRange(0, editInput.value.length);
	}

	function handleEditKeydown(e: KeyboardEvent, index: number) {
		if (disabled) return;
		// space, comma, semicolon, and enter
		if (e.key.match(/[\s,;]|Enter/)) {
			if (editInput && (editInput?.value || e.key === " ")) {
				pendingTokens[index] = editInput.value;

				// Turn off all editing states
				Object.keys(editState).forEach(stringToken => {
					editState[stringToken] = { ...editState[stringToken], isEditing: false };
				});

				e.preventDefault();
			}
		}

		if (editInput) {
			editInput.size = Math.max(1, editInput.value.length) + 4;
		}
	}

	function handleEditFinish(e: FocusEvent, index: number) {
		if (disabled) return;
		const target = e.target as HTMLInputElement;
		const value = target.value;

		pendingTokens[index] = value;
		editState[value] = { ...editState[value], isEditing: false };
		editState = { ...editState };
	}

	function handleInputFocus() {
		isInputFocused = true;
		//if there is only one token, delete it and allow the input to handle add it when done editing
		if (!explicitlyEnteringMultiple) {
			pendingTokens = [];
		}
	}
</script>

<div class="w-full">
	<!-- svelte-ignore a11y_click_events_have_key_events -->
	<div
		class={twMerge("outline-primary-500 focus:border-primary-500 relative h-auto overflow-auto rounded border border-gray-300 bg-gray-50 px-3 py-2 pb-0 outline-1 focus:outline", isInputFocused && "border-primary-500 outline-primary-500 outline", disabled && "h-auto border-none bg-white outline-none", useGrayOutline && "border-none outline-gray-300", "max-h-[70px]")}
		tabindex="0"
		role="listbox"
		aria-multiselectable="true"
		aria-orientation="horizontal"
		aria-label="Search Field"
		onclick={focusInput}
		data-action-name="multi-string-editor-container"
		data-action-title={context}
	>
		<ul class="relative flex flex-row flex-wrap items-start">
			{#if explicitlyEnteringMultiple}
				{#each pendingTokens as stringToken, index (`${stringToken}-${index}`)}
					<li class="mr-1.5 mb-1.5 inline-flex shrink-0 whitespace-nowrap">
						{#if editState[stringToken]?.isEditing}
							<input
								bind:this={editInput}
								size={stringToken.length}
								onfocus={handleEditFocus}
								onkeydown={e => handleEditKeydown(e, index)}
								onblur={e => handleEditFinish(e, index)}
								value={stringToken}
								class={twMerge("inline-flex max-w-full border-0 bg-gray-50 px-[2px] py-[3px] text-sm font-normal text-gray-900 focus:[box-shadow:none]", disabled && "bg-white", "text-xs")}
								type="text"
								data-action-name="multi-string-editor-input"
								data-action-title={context}
							/>
						{:else}
							<Badge
								dismissable
								border
								color="secondary"
								class="my-0 border-gray-500 px-2 py-0"
								onclose={e => initiateEdit(e, stringToken)}
								onmouseenter={() => (editState[stringToken] = { ...editState[stringToken], selected: true })}
								onmouseleave={() => (editState[stringToken] = { ...editState[stringToken], selected: false })}
								onfocus={() => (editState[stringToken] = { ...editState[stringToken], selected: true })}
								data-action-name="multi-string-editor-edit-individual-string"
								data-action-title={context}
								onclick={e => initiateEdit(e, stringToken)}
							>
								{#snippet icon()}
									{#if !disabled}
										<button
											class="flex h-4 w-4 items-center"
											type="button"
											onclick={e => {
												e.stopPropagation();
												removeStringToken(index);
											}}
											data-action-name="multi-string-editor-remove-individual-string"
											data-action-title={context}
										>
											<span class="sr-only">Remove {stringToken}</span>
											<Icon class="h-4 w-4 text-black" name="x-mark" />
										</button>
									{/if}
								{/snippet}
								<P size="xs">{stringToken}</P>
							</Badge>
						{/if}
					</li>
				{/each}
			{/if}
			{#if !disabled}
				<li class="mr-2 mb-2 inline-flex whitespace-nowrap">
					<input
						name="pending"
						bind:this={input}
						onkeydown={handleKeydown}
						onblur={handleInputBlur}
						onpaste={handlePaste}
						onfocus={handleInputFocus}
						class={twMerge("inline-flex max-w-full border-0 bg-gray-50 p-0 text-xs font-normal text-gray-900 focus:[box-shadow:none]", stringTokens.length === 0 && pendingTokens.length === 0 ? "min-w-[150px]" : "mt-1")}
						type="text"
						placeholder={pendingTokens.length === 0 ? "Enter or paste in value(s)" : undefined}
					/>
				</li>
			{/if}
		</ul>
	</div>
</div>
